import { useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { EmptyStateMessage } from '@/components/common';
import { useUpdateInterviewCandidate } from '@/queries/useAppraisalDetail';
import type { InterviewCandidate } from '@/api/types/appraisal.types';
import type { SectionAccess } from '@/auth/permissions.types';

interface InterviewCandidatesTableProps {
  candidates: InterviewCandidate[];
  sectionAccess: SectionAccess | undefined;
  appraisalId: number;
  rowVersion?: string;
}

export function InterviewCandidatesTable({
  candidates,
  sectionAccess,
  appraisalId,
  rowVersion,
}: InterviewCandidatesTableProps) {
  const [showInterviews, setShowInterviews] = useState(true);
  const { mutate: updateCandidate } = useUpdateInterviewCandidate();

  const canEditSuggested = sectionAccess?.canEditSuggested ?? false;
  const canEditPlanned = sectionAccess?.canEditPlanned ?? false;
  const canEditReason = canEditSuggested;

  const handleCheckboxChange = (
    candidateId: number,
    field: 'appraiseSuggested' | 'planned',
    value: boolean
  ) => {
    updateCandidate({
      appraisalId,
      candidateId,
      data: { [field]: value },
      rowVersion,
    });
  };

  const handleReasonChange = (candidateId: number, reason: string, originalReason: string | null) => {
    const newReason = reason || null;
    if (newReason === originalReason) return;
    updateCandidate({
      appraisalId,
      candidateId,
      data: { reason: newReason },
      rowVersion,
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          Interview Candidates
        </Typography>
        <MuiLink
          component="button"
          variant="body2"
          onClick={() => setShowInterviews((prev) => !prev)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              transform: showInterviews ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
          {showInterviews ? 'Hide interviews' : 'Show interviews'}
        </MuiLink>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box
          sx={{
            width: 14,
            height: 14,
            bgcolor: '#FFE082',
            border: '1px solid #C2C7C8',
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          Was Job End author
        </Typography>
      </Box>
      <Collapse in={showInterviews}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '25%' }}>Name</TableCell>
              <TableCell sx={{ width: '15%' }}>Role</TableCell>
              <TableCell sx={{ width: '30%' }}>Reason</TableCell>
              <TableCell sx={{ width: '15%' }} align="center">Appraisee Suggestion</TableCell>
              <TableCell sx={{ width: '15%' }} align="center">Planned</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <EmptyStateMessage message="No interview candidates." />
                </TableCell>
              </TableRow>
            ) : (
              candidates.map((candidate) => (
              <TableRow key={candidate.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      aria-label={candidate.isJobEndAuthor ? 'Job end author' : undefined}
                      sx={{
                        width: 10,
                        height: 10,
                        bgcolor: candidate.isJobEndAuthor ? '#FFE082' : 'transparent',
                        border: candidate.isJobEndAuthor ? '1px solid #C2C7C8' : 'none',
                        flexShrink: 0,
                      }}
                    />
                    {candidate.staffName}
                  </Box>
                </TableCell>
                <TableCell>{candidate.staffRole || '-'}</TableCell>
                <TableCell>
                  {canEditReason ? (
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      defaultValue={candidate.reason ?? ''}
                      onBlur={(e) => handleReasonChange(candidate.id, e.target.value, candidate.reason)}
                      inputProps={{ 'aria-label': `Reason for ${candidate.staffName}` }}
                    />
                  ) : (
                    candidate.reason || '-'
                  )}
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={candidate.appraiseSuggested}
                    disabled={!canEditSuggested}
                    onChange={(e) =>
                      handleCheckboxChange(candidate.id, 'appraiseSuggested', e.target.checked)
                    }
                    inputProps={{ 'aria-label': `Suggest ${candidate.staffName}` }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    checked={candidate.planned}
                    disabled={!canEditPlanned}
                    onChange={(e) =>
                      handleCheckboxChange(candidate.id, 'planned', e.target.checked)
                    }
                    inputProps={{ 'aria-label': `Plan ${candidate.staffName}` }}
                  />
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
}

export default InterviewCandidatesTable;
